/**
 * QuestionDeepDive
 *
 * Renders one Phase 2 primary question with dual-audience gating:
 *
 *   Candidate view:
 *     - Always visible: question text, interviewer intent
 *     - Gated (until submission OR manual reveal):
 *       exemplar answer, what to listen for, calibration note
 *     - CTA: "Submit for Evaluation" (routes to agentic evaluator)
 *     - Escape hatch: "Reveal exemplar without submitting" (with confirmation)
 *
 *   Hiring Manager view:
 *     - All sections visible (HMs are the evaluators; they need calibration
 *       material)
 *
 * GATING PERSISTENCE
 * ─────────────────
 * Submission state is tracked in localStorage under the key
 *   `course-submitted:{slug}` = "true"
 *
 * This is the MVP approach. When we ship server-authoritative gating, the
 * localStorage check becomes a React Query fetch against a backend endpoint
 * (e.g. `GET /api/v1/course/submissions`) — the component surface does not
 * change.
 *
 * ROUTING TO EVALUATOR
 * ────────────────────
 * "Submit for Evaluation" navigates to `/evaluate` with `location.state`
 * carrying:
 *   - questionText:   prefills the question field
 *   - track:          always "agentic" for Phase 2 questions
 *   - interviewType:  "behavioral" or "system_design" per framework
 *   - courseSlug:     identifies this as a Phase 2 course submission so
 *                     NewEvaluation can set the localStorage flag on success
 *
 * NewEvaluation.tsx needs to be updated separately to read these fields
 * from location.state. Until that update lands, the user will need to set
 * track manually in the evaluator — this component is the preparation layer.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HearingIcon from "@mui/icons-material/Hearing";
import TuneIcon from "@mui/icons-material/Tune";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import type { Phase2Question } from "../data/phase2Questions";

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const storageKey = (slug: string) => `course-submitted:${slug}`;

function hasSubmittedAnswer(slug: string): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(storageKey(slug)) === "true";
}

function markSubmitted(slug: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey(slug), "true");
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  question: Phase2Question;
  audience: "candidate" | "hiringManager";
  levelColor: string;
  canSubmit: boolean;   // false when user is not logged in / not subscribed
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function QuestionDeepDive({
  question,
  audience,
  levelColor,
  canSubmit,
}: Props) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const [submitted, setSubmitted] = useState(() => hasSubmittedAnswer(question.slug));
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);

  // Hiring managers always see exemplar (they're the evaluator)
  // Candidates see exemplar only after submission or manual reveal
  const showExemplar = audience === "hiringManager" || submitted;

  // ─── Navigation handler ──────────────────────────────────────────────────
  const handleSubmitForEvaluation = () => {
    // Mark as submitted locally — the actual evaluation happens on /evaluate.
    // This optimistically unlocks the exemplar so when the user returns from
    // /evaluate, they see the exemplar (and can compare their answer to it).
    markSubmitted(question.slug);
    setSubmitted(true);

    navigate("/evaluate", {
      state: {
        questionText: question.questionText,
        track: "agentic",
        interviewType: question.interviewType,
        courseSlug: question.slug,
      },
    });
  };

  // ─── Manual reveal handler (with confirmation) ───────────────────────────
  const handleReveal = () => {
    markSubmitted(question.slug);
    setSubmitted(true);
    setRevealDialogOpen(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${submitted && audience === "candidate"
          ? alpha(levelColor, 0.35)
          : theme.palette.divider}`,
        borderRadius: 2,
        p: 3,
        transition: "border-color 0.3s",
      }}
    >
      {/* ── Header: slug chip + domain + interview type ─────────────────── */}
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
        <Chip
          label={question.slug.toUpperCase()}
          size="small"
          sx={{
            fontFamily: "monospace",
            fontWeight: 500,
            fontSize: "0.7rem",
            bgcolor: alpha(levelColor, 0.12),
            color: levelColor,
          }}
        />
        <Chip
          label={`${question.domainCode} · ${question.domainName}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.7rem", borderColor: theme.palette.divider }}
        />
        <Chip
          label={
            question.interviewType === "system_design"
              ? t("QuestionDeepDive.systemDesign")
              : t("QuestionDeepDive.behavioral")
          }
          size="small"
          sx={{
            fontSize: "0.7rem",
            bgcolor: question.interviewType === "system_design"
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.info.main, 0.1),
            color: question.interviewType === "system_design" ? theme.palette.primary.main : theme.palette.info.dark,
            fontWeight: 500,
          }}
        />
        {submitted && audience === "candidate" && (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: "0.9rem !important" }} />}
            label={t("QuestionDeepDive.submitted")}
            size="small"
            sx={{
              ml: "auto",
              fontSize: "0.7rem",
              bgcolor: alpha(levelColor, 0.12),
              color: levelColor,
              fontWeight: 500,
            }}
          />
        )}
      </Stack>

      {/* ── Question text ───────────────────────────────────────────────── */}
      <Typography
        variant="body1"
        fontWeight={500}
        sx={{ lineHeight: 1.55, mb: 2.5, color: "text.primary" }}
      >
        {question.questionText}
      </Typography>

      {/* ── Interviewer intent (always visible) ─────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.info.main, 0.04),
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          borderRadius: 2,
          p: 2,
          mb: 2.5,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <LightbulbOutlinedIcon sx={{ color: theme.palette.info.dark, fontSize: 18, mt: "2px", flexShrink: 0 }} />
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.info.dark, letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              {t("QuestionDeepDive.whyInterviewersAsk")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, mt: 0.5 }}>
              {question.interviewerIntent}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* ── Gated content (exemplar + what to listen for + calibration) ── */}
      {showExemplar ? (
        <Stack spacing={2}>
          {/* Exemplar answer */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: alpha(levelColor, 0.04),
              border: `1px solid ${alpha(levelColor, 0.25)}`,
              borderRadius: 2,
              p: 2.5,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <CheckCircleIcon sx={{ color: levelColor, fontSize: 18 }} />
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: levelColor, letterSpacing: "0.04em", textTransform: "uppercase" }}
              >
                {t("QuestionDeepDive.exemplarAnswer", { seniority: question.seniority })}
              </Typography>
            </Stack>
            {question.exemplarAnswer.split("\n\n").map((para, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ lineHeight: 1.7, mb: 1.5, color: "text.primary", "&:last-child": { mb: 0 } }}
              >
                {para}
              </Typography>
            ))}
          </Paper>

          {/* What to Listen For */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: alpha(theme.palette.warning.main, 0.04),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
              borderRadius: 2,
              p: 2,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <HearingIcon sx={{ color: theme.palette.warning.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: theme.palette.warning.main, letterSpacing: "0.04em", textTransform: "uppercase" }}
                >
                  {t("QuestionDeepDive.whatToListenFor")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, mt: 0.5 }}>
                  {question.whatToListenFor}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Calibration Note (optional) */}
          {question.calibrationNote && (
            <Paper
              elevation={0}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <TuneIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: theme.palette.primary.main, letterSpacing: "0.04em", textTransform: "uppercase" }}
                  >
                    {t("QuestionDeepDive.calibrationNote")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, mt: 0.5 }}>
                    {question.calibrationNote}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Show the "Submitted answer" render slot only for candidates (HMs don't need it) */}
          {audience === "candidate" && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  p: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: "text.disabled",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {t("QuestionDeepDive.yourSubmittedAnswer")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                    lineHeight: 1.6,
                    mt: 0.5,
                  }}
                >
                  {question.submittedAnswer}
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      ) : (
        // ── Gated state (candidate has not submitted) ───────────────────
        <Box>
          <Paper
            elevation={0}
            sx={{
              textAlign: "center",
              p: 3,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 28, color: "text.disabled", mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 460, mx: "auto" }}>
              {t("QuestionDeepDive.gatedHint")}
            </Typography>
            {canSubmit ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleSubmitForEvaluation}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: levelColor,
                    fontWeight: 500,
                    "&:hover": { bgcolor: alpha(levelColor, 0.85) },
                  }}
                >
                  {t("QuestionDeepDive.submitForEvaluation")}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setRevealDialogOpen(true)}
                  sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                >
                  {t("QuestionDeepDive.revealWithoutSubmitting")}
                </Button>
              </Stack>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2, display: "inline-flex" }}>
                {t("QuestionDeepDive.allAccessOnly")}
              </Alert>
            )}
          </Paper>
        </Box>
      )}

      {/* ── Reveal confirmation dialog ─────────────────────────────────── */}
      <Dialog open={revealDialogOpen} onClose={() => setRevealDialogOpen(false)}>
        <DialogTitle>{t("QuestionDeepDive.revealDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans
              i18nKey="QuestionDeepDive.revealDialogBody"
              t={t}
              components={[<em key="em" />, <br key="br1" />, <br key="br2" />]}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevealDialogOpen(false)}>{t("QuestionDeepDive.back")}</Button>
          <Button onClick={handleReveal} variant="contained" color="warning">
            {t("QuestionDeepDive.revealAnyway")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
